import type { Role } from './types.js';

// Single source of truth for capabilities. UI and Cloud Functions both import this.
// firestore.rules mirrors it; tests/rules verify.
export const PERMISSIONS = [
  'members.read', 'members.write', 'members.delete', 'members.readSensitive',
  'memberships.write', 'plans.manage',
  'billing.read', 'billing.charge', 'billing.refund',
  'schedule.manage', 'sessions.cancel',
  'bookings.manageAny', 'attendance.checkInAny', 'attendance.correct',
  'progress.assess', 'progress.promote', 'curriculum.manage',
  'leads.manage', 'comms.send', 'automations.manage',
  'pos.sell', 'inventory.manage', 'events.manage',
  'reports.view', 'reports.financial', 'tasks.manage',
  'settings.manage', 'roles.manage', 'audit.read', 'data.export',
] as const;
export type Permission = typeof PERMISSIONS[number];

const ALL = [...PERMISSIONS];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: ALL,
  admin: ALL.filter(p => !['roles.manage', 'data.export', 'members.delete'].includes(p)),
  instructor: ['members.read', 'members.readSensitive', 'attendance.checkInAny', 'attendance.correct', 'bookings.manageAny',
    'progress.assess', 'progress.promote', 'sessions.cancel', 'comms.send', 'reports.view', 'tasks.manage'],
  frontDesk: ['members.read', 'members.write', 'memberships.write', 'billing.read', 'billing.charge',
    'bookings.manageAny', 'attendance.checkInAny', 'leads.manage', 'pos.sell', 'tasks.manage'],
  member: [], guardian: [], prospect: [],
};

export const STAFF_ROLES: Role[] = ['owner', 'admin', 'instructor', 'frontDesk'];

/** Owner can deny specific permissions to admins (settings/roles.adminDenied). */
export function can(roles: Role[], perm: Permission, denied: Permission[] = []): boolean {
  if (denied.includes(perm) && !roles.includes('owner')) return false;
  return roles.some(r => ROLE_PERMISSIONS[r]?.includes(perm));
}

export const isStaff = (roles: Role[]) => roles.some(r => STAFF_ROLES.includes(r));

/** Only owner can grant owner/admin. Last-owner protection is enforced server-side. */
export function canAssignRole(actorRoles: Role[], target: Role): boolean {
  if (!can(actorRoles, 'roles.manage')) return false;
  return actorRoles.includes('owner') || !['owner', 'admin'].includes(target);
}
